// LISTA-SZŰRŐK AZ URL-BEN (TanStack Router search params) — a `apps/web/CLAUDE.md` state-konvenciója:
//   „Server state = TanStack Query · **URL state = search params (filter, pagináció, tab)** · lokális = useState".
//
// MIÉRT NEM `useState` (a korábbi állapot): a szűrő `useState`-ben él, tehát (a) a szűrt lista NEM linkelhető
// — egy „nézd meg ezt a szűrést" üzenet a nyers listára visz —, (b) az F5 / a vissza-nyíl / a detail-lapról
// való visszatérés ELDOBJA a szűrést, és (c) a támogatás nem tudja megkérni a felhasználót, hogy küldje el a
// pontos nézetét. Ez mind ugyanaz a hiba-osztály: a nézet állapota nem címezhető.
//
// A SZERZŐDÉS:
//  • a `defaults` a szűrő-kulcsok EGYETLEN forrása (a route `validateSearch`-e ugyanezeket a kulcsokat engedi),
//  • ami a defaulttal egyenlő, az NEM megy az URL-be (tiszta, olvasható link: `?q=hotel`, nem `?q=hotel&type=`),
//  • a navigáció `replace` — a szűrő-váltás NEM history-bejegyzés: 20 karakter beírása különben 20 „vissza"
//    lépést jelentene, és a vissza-nyíl a felhasználó szerint az OLDALT hagyja el, nem a szűrőt vonja vissza,
//  • a hook NEM debounce-ol: a beviteli mező azonnali (lokális) állapota a hívóé, az URL-be a debounce-olt
//    érték kerül (a lista-lekérdezés kulcsa is az). A kettő szétválasztása szándékos.
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

/** Egy szűrő-készlet: kulcs → érték. Csak STRING (az URL is az); a szám/bool a hívó dolga. */
export type ListFilterValues = Record<string, string>;

export interface UseListFiltersResult<T extends ListFilterValues> {
  /** Az aktuális szűrők (URL ∪ default). */
  filters: T;
  /** Egy vagy több szűrő állítása (a többi változatlan). A defaultra állított kulcs kiesik az URL-ből. */
  setFilters: (patch: Partial<T>) => void;
  /** Minden szűrő vissza a defaultra (az URL-ből kiesnek). A NEM-szűrő paramok (pl. `open`) érintetlenek. */
  resetFilters: () => void;
}

/**
 * A lista-szűrők az URL search-paramokból, a `defaults` kulcs-készletével.
 *
 * A hook ROUTER-KONTEXTUST igényel (a lista-oldal része a route-nak). Teszt-környezetben a
 * `src/test/render-with-router.tsx` adja meg a keretet — így a page-komponens a valódi, router-tudatos
 * viselkedését teszteli, nem egy leegyszerűsített, prop-injektált változatot.
 */
/**
 * TÖBB-ÉRTÉKŰ szűrő az URL-ben: vesszővel elválasztott lista ↔ tömb.
 *
 * MIÉRT vessző, és nem ismételt query-paraméter (`?st=active&st=pending`): a `useListFilters` szerződése
 * STRING-értékű kulcsokra szól (az URL is az), és a vesszős alak olvasható marad egy megosztott linkben is.
 * A szűrő-értékek enum-ok és azonosítók — vesszőt egyik sem tartalmaz.
 */
/** A szűrő-értékek egy TETSZŐLEGES search-objektumból (URL ∪ default) — egy forrás az olvasásra. */
function readValues(source: Record<string, unknown>, defaults: ListFilterValues): ListFilterValues {
  const out: ListFilterValues = {};
  for (const [key, fallback] of Object.entries(defaults)) {
    const raw = source[key];
    out[key] = typeof raw === 'string' && raw !== '' ? raw : fallback;
  }
  return out;
}

export function parseListParam(raw: string): string[] {
  return raw === '' ? [] : raw.split(',').filter((v) => v !== '');
}

/** A `parseListParam` párja: tömb → URL-alak (üres tömb → `''`, ami a default → kiesik az URL-ből). */
export function formatListParam(values: readonly string[]): string {
  return values.join(',');
}

export function useListFilters<T extends ListFilterValues>(defaults: T): UseListFiltersResult<T> {
  // `strict: false`: a hook route-független (bármelyik lista-oldalon ugyanígy működik).
  const search: Record<string, unknown> = useSearch({ strict: false });
  const navigate = useNavigate();

  const filters = useMemo(() => readValues(search, defaults) as T, [search, defaults]);

  /** A default-értékű kulcsokat `undefined`-re állítjuk → a router kihagyja őket az URL-ből. */
  const toSearchPatch = useCallback(
    (next: ListFilterValues): Record<string, undefined | string> => {
      const patch: Record<string, undefined | string> = {};
      for (const [key, fallback] of Object.entries(defaults)) {
        const value = next[key] ?? fallback;
        patch[key] = value === fallback || value === '' ? undefined : value;
      }
      return patch;
    },
    [defaults],
  );

  const readFrom = useCallback(
    (source: Record<string, unknown>): ListFilterValues => readValues(source, defaults),
    [defaults],
  );

  /**
   * ⚠️ A KIINDULÓ ÉRTÉK A `prev`, NEM a render-időbeli `filters` — és ez nem stílus-kérdés.
   *
   * MÉRT HIBA: a `toSearchPatch` MINDEN szűrő-kulcsot kiír, nem csak a patchelteket. Amíg a
   * kiindulás a renderhez tartozó `filters` pillanatfelvétel volt, EGY renderben indított két hívás közül a
   * második visszaírta az első ELŐTTI állapotot — tehát a második felülvágta az elsőt. A `ListToolbar`
   * „Szűrők törlése" sora pontosan ezt teszi (szűrőnként egy `onChange`), ezért KÉT aktív szűrőből CSAK AZ
   * EGYIK törlődött; a kaszkád-resetek (fő-kategória → alkategória + szolgáltatás + csomag) ugyanígy
   * hullottak szét. A `prev`-ből olvasva a hívások EGYMÁSRA épülnek.
   *
   * Ettől függetlenül: ami ATOMI szándék, azt EGY hívásban add át (`setFilters({ a, b, c })`) — az egy
   * navigációt jelent, nem hármat.
   */
  const setFilters = useCallback(
    (patch: Partial<T>) => {
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          ...toSearchPatch({ ...readFrom(prev), ...(patch as ListFilterValues) }),
        }),
        replace: true,
      });
    },
    [navigate, readFrom, toSearchPatch],
  );

  const resetFilters = useCallback(() => {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, ...toSearchPatch(defaults) }),
      replace: true,
    });
  }, [navigate, defaults, toSearchPatch]);

  return { filters, setFilters, resetFilters };
}
